// Decides whether a failed staff API response means the session is dead, and
// if so why. Pure, so it is unit tested in isolation (sessionEnd.test.js); the
// axios interceptor in AuthContext only gathers the inputs and acts on the
// answer.
//
// Only three status and message pairs qualify. All three come from
// authenticateToken in server/middleware/auth.js and mean this token will never
// work again:
//   401 "Session is no longer valid"  revoked (password change or reset, email
//                                     change, role change, team removal)
//   401 "Token expired"               jwt.verify TokenExpiredError
//   403 "Invalid token"               any other jwt.verify failure
// Every other 401 or 403 is left alone: wrong-password answers on the auth
// forms, role and tier refusals, plan limits and the trial gate are all 403s
// with other messages, and "Access token required" means the request was sent
// without the token, which says nothing about the token itself.

export const SESSION_END = {
  REVOKED: 'revoked',
  EXPIRED: 'expired',
};

const QUALIFYING = [
  { status: 401, message: 'Session is no longer valid', reason: SESSION_END.REVOKED },
  { status: 401, message: 'Token expired', reason: SESSION_END.EXPIRED },
  { status: 403, message: 'Invalid token', reason: SESSION_END.EXPIRED },
];

// Staff auth forms. None of them can return a qualifying pair today; listed so
// that a future change to their error text cannot turn a wrong password into a
// sign-out.
const AUTH_FORM_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-reset-token',
];

// The customer portal has its own token and its own sign-out
// (PortalAuthContext). Its middleware also answers 401 "Session is no longer
// valid", so it is excluded by path rather than by message.
const PORTAL_API_PATH = '/api/portal/';

// Pages a signed-out visitor can be on. A failure there is that page's own
// business, and skipping /login is what rules out a redirect loop.
const PUBLIC_PAGE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/pricing',
  '/new-customer/',
  '/signup/',
  '/join/',
  '/portal',
  '/privacy',
  '/terms',
  '/delete-account',
];

const isPublicPage = (pathname) => {
  if (!pathname || pathname === '/') return true;
  // '/portal' covers '/portal' and '/portal/...', never '/portalx'.
  return PUBLIC_PAGE_PREFIXES.some((p) => (
    p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`)
  ));
};

const urlPath = (url) => {
  if (!url) return '';
  try {
    return new URL(url, 'http://placeholder.invalid').pathname;
  } catch (e) {
    return String(url);
  }
};

// Returns SESSION_END.REVOKED, SESSION_END.EXPIRED, or null to leave the
// session alone.
//
// requestToken is the bearer token the failed request carried, storedToken the
// one in localStorage now. Both must be present and equal: no stored token
// means there is no session to end (and is what makes a burst of failures sign
// out only once, since the first one clears it), and a different stored token
// means the session was already replaced, as when this tab changed its own
// password and stored the fresh token while an older request was in flight.
export const sessionEndReason = ({ status, message, url, requestToken, storedToken, pathname }) => {
  if (!storedToken || !requestToken || requestToken !== storedToken) return null;

  const path = urlPath(url);
  if (path.includes(PORTAL_API_PATH)) return null;
  if (AUTH_FORM_PATHS.some((p) => path.endsWith(p))) return null;
  if (isPublicPage(pathname)) return null;

  const match = QUALIFYING.find((q) => q.status === status && q.message === message);
  return match ? match.reason : null;
};

// The bearer token from a request's headers. axios 1.x hands back an
// AxiosHeaders instance; a plain object is accepted too.
export const bearerFrom = (headers) => {
  if (!headers) return null;
  const value = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : headers.Authorization || headers.authorization;
  if (typeof value !== 'string') return null;
  const [scheme, token] = value.split(' ');
  return scheme === 'Bearer' && token ? token : null;
};

// One-time notice for the login page. sessionStorage, read once and removed,
// so it shows on the redirect and not on any later visit.
export const SIGNED_OUT_KEY = 'signedOutReason';

export const SIGNED_OUT_MESSAGES = {
  [SESSION_END.REVOKED]: 'You were signed out because your account was changed on another device. Please sign in again.',
  [SESSION_END.EXPIRED]: 'Your session has expired. Please sign in again.',
};

export const rememberSignedOut = (reason) => {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, reason);
  } catch (e) {
    /* Storage unavailable: the redirect still happens, just without the line. */
  }
};

// Read and forget are separate so the login page can read in a state
// initializer (which StrictMode runs twice) and forget in an effect.
export const peekSignedOutMessage = () => {
  try {
    return SIGNED_OUT_MESSAGES[sessionStorage.getItem(SIGNED_OUT_KEY)] || null;
  } catch (e) {
    return null;
  }
};

export const forgetSignedOut = () => {
  try {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch (e) {
    /* Nothing useful to do. */
  }
};
